import React from 'react';
import { Card } from '../components/ui/Card';
import { InteractiveFlow } from '../components/dashboard/InteractiveFlow';
import { VisualCard } from '../components/ui/VisualCard';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiCpu, FiLayout, FiTruck, FiArrowRight } from 'react-icons/fi';

export const HowItWorks = () => {
  return (
    <div className="p-6 space-y-12 max-w-6xl mx-auto">
      <header className="relative py-16 px-8 rounded-2xl bg-gradient-to-b from-chefDark to-chefBlack border border-chefPanel overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
           <FiCpu size={240} className="text-chiliRed" />
        </div>
        <div className="relative z-10 text-center">
          <div className="inline-block px-3 py-1 bg-chiliRed/10 border border-chiliRed/20 rounded-full text-chiliRed text-[10px] font-mono uppercase tracking-[0.2em] mb-6">System Architecture v4.2</div>
          <h1 className="text-5xl font-black text-receiptWhite tracking-tighter mb-4">THE NEURAL LINK FOR <br/>HIGH-STAKES SERVICE</h1>
          <p className="text-receiptMuted text-lg max-w-2xl mx-auto leading-relaxed">
            TableTurn eliminates the "Service Gap" between the host stand and the kitchen pass using a unified command infrastructure.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <FeatureCard 
          type="dashboard"
          icon={FiLayout} 
          title="FOH Precision" 
          desc="Dynamic floorplans that react to real-time service pace, not just table occupancy."
        />
        <FeatureCard 
          type="kitchen"
          icon={FiCpu} 
          title="BOH Intelligence" 
          desc="Prep lanes prioritized by turn-time targets and real-time seating velocity."
        />
        <FeatureCard 
          type="inventory"
          icon={FiTruck} 
          title="Supply Sync" 
          desc="Inventory levels that auto-deplete as tickets are fired, triggering smart 86 alerts."
        />
      </div>

      <div className="space-y-6">
        <div className="flex items-center gap-4 px-2">
          <div className="h-px flex-1 bg-chefPanel"></div>
          <h2 className="text-receiptWhite font-mono text-sm tracking-[0.4em] uppercase">Interactive Simulation</h2>
          <div className="h-px flex-1 bg-chefPanel"></div>
        </div>
        <InteractiveFlow />
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-12 border-t border-chefPanel/50">
        <div className="space-y-6">
          <h3 className="text-3xl font-bold text-receiptWhite tracking-tight">Engineered for Results</h3>
          <div className="grid grid-cols-1 gap-6">
            <OutcomeItem title="12% Faster Turns" desc="Optimized clearing/seating flows reduce idle table time by an average of 4.2 minutes per shift." />
            <OutcomeItem title="86% Less Waste" desc="Direct kitchen-to-server communication prevents orders for items that have just run out." />
            <OutcomeItem title="Unified Command" desc="One source of truth for shift summaries, labor planning, and guest satisfaction metrics." />
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-chiliRed/5 blur-3xl rounded-full"></div>
          <VisualCard type="dashboard" title="Live Operations Heatmap" className="h-80 shadow-2xl" />
        </div>
      </section>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc, type }) => (
  <div className="flex flex-col gap-6 p-1 h-full">
    <VisualCard type={type} title={title} className="h-48" />
    <div className="px-4 pb-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-chiliRed"><SafeIcon icon={icon} /></div>
        <h4 className="text-receiptWhite font-bold text-lg">{title}</h4>
      </div>
      <p className="text-receiptMuted text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

const OutcomeItem = ({ title, desc }) => (
  <div className="bg-chefDark/50 border border-chefPanel p-5 rounded-xl hover:border-chiliRed/30 transition-all group">
    <div className="flex gap-4">
      <div className="mt-1 text-status-open group-hover:scale-110 transition-transform"><SafeIcon icon={FiCheck} /></div>
      <div>
        <h5 className="text-receiptWhite font-mono text-sm uppercase tracking-wider">{title}</h5>
        <p className="text-receiptMuted text-xs mt-2 leading-relaxed">{desc}</p>
      </div>
    </div>
  </div>
);